import { AzzurroHubDataType, ConnextData, getRequestTzMemoized } from "ngx-core-services";
import { IANAZone } from "luxon";

import { SystemDefinition } from "../../state/system.state";

export const checkAndUpdateVirtualSystemTz = async (system: SystemDefinition): Promise<SystemDefinition> => {
  const coordinates = system.configuration.activeZone?.coordinates;
  const now = Date.now();

  if (!coordinates) {
    throw new Error(`No coordinates for system ${system.uid}`);
  }

  const firebaseTz = (system.configuration.activeZone?.tz || 0) / 4;

  if (
    system.configuration.timezoneEnd &&
    system.configuration.timezoneEnd > now &&
    system.configuration.activeZone?.tz !== undefined &&
    Number.isInteger(firebaseTz)
  ) {
    return {
      ...system,
      metadata: {
        ...system.metadata || {},
        tz: firebaseTz,
        structure: system.metadata?.structure || 2,
      }
    }
  }

  const tzResponse = await getRequestTzMemoized(coordinates);
  if (!tzResponse) {
    throw new Error('Timezone response is invalid');
  }

  return {
    ...system,
    configuration: {
      ...system.configuration,
      activeZone: {
        ...system.configuration.activeZone!,
        tz: (tzResponse.gmtOffset / 3600) * 4,
      },
      timezoneEnd: tzResponse.zoneEnd * 1000,
    },
    metadata: {
      ...system.metadata || {},
      tz: tzResponse.gmtOffset / 3600,
      /**
       * Default to WithBattery
       */
      structure: system.metadata?.structure || 2,
    },
  };
}

export const checkAndUpdateAzzurroHubSystemTz = async (system: SystemDefinition): Promise<SystemDefinition> => {
  const azzurroHubData = system.data as Partial<AzzurroHubDataType>;
  const iana = azzurroHubData?.config?.IANA;
  const zone = IANAZone.create(iana || 'Europe/Rome');
  const offsetOnHours = zone.offset(new Date().getTime()) / 60;

  return {
    ...system,
    configuration: {
      ...system.configuration,
      activeZone: {
        ...system.configuration.activeZone!,
        tz: offsetOnHours,
      },
      timezoneEnd: new Date().setHours(0, 0, 0, 0),
    },
    metadata: {
      ...system.metadata || {},
      tz: offsetOnHours,
      structure: system.metadata?.structure || 2,
    },
  };
}
export const checkAndUpdateConnextSystemTz = async (system: SystemDefinition): Promise<SystemDefinition> => {
  const connextData = system.data as Partial<ConnextData>;
  const tzDefault = 2;
  const timezoneEndDefault = new Date().setHours(0, 0, 0, 0);

  const connextLatitude = connextData?.config?.latitude as number;
  const connextLongitude = connextData?.config?.longitude as number;
  const connextCoordinates = `${connextLatitude},${connextLongitude}`;

  if (
    isNaN(connextLatitude) || isNaN(connextLongitude) ||
    connextLatitude < -90 || connextLatitude > 90 ||
    connextLongitude < -180 || connextLongitude > 180 ||
    connextLatitude === 0 || connextLongitude === 0
  ) {
    return {
      ...system,
      configuration: {
        ...system.configuration,
        activeZone: {
          ...system.configuration.activeZone!,
          tz: tzDefault,
        },
        timezoneEnd: timezoneEndDefault,
      },
      metadata: {
        ...system.metadata || {},
        tz: tzDefault,
        /**
         * Default to WithBattery
         */
        structure: system.metadata?.structure || 2,
      },
    };
  }

  const systemCoordinates = system.configuration.activeZone?.coordinates;
  const now = Date.now();

  if (systemCoordinates !== connextCoordinates || (system.configuration.timezoneEnd && system.configuration.timezoneEnd < now)) {
    const tzResponse = await getRequestTzMemoized(connextCoordinates);
    if (!tzResponse) {
      throw new Error('Timezone response is invalid');
    }

    return {
      ...system,
      configuration: {
        ...system.configuration,
        activeZone: {
          ...system.configuration.activeZone!,
          tz: tzResponse.gmtOffset / 3600,
        },
        timezoneEnd: tzResponse.zoneEnd * 1000,
      },
      metadata: {
        ...system.metadata || {},
        tz: tzResponse.gmtOffset / 3600,
        /**
         * Default to WithBattery
         */
        structure: system.metadata?.structure || 2,
      },
    };
  }

  return system;
}

export const checkAndUpdateSystemTz = (system: SystemDefinition): Promise<SystemDefinition> => {
  if (system.monitoringType === 'Connext') {
    return checkAndUpdateConnextSystemTz(system);
  }

  if (system.monitoringType === 'AzzurroHub') {
    return checkAndUpdateAzzurroHubSystemTz(system);
  }

  return checkAndUpdateVirtualSystemTz(system);
}
